import { ReloadOutlined } from "@ant-design/icons";
import { useAuthFetch } from "@p0security/frontend-core";
import {
  useFirestoreCollection,
  useFirestoreDoc,
} from "@p0security/frontend-core/react";
import { toRequestAttribution } from "@p0security/shared/permission-requests/attribution";
import { convertToDurationOption } from "@p0security/shared/permission-requests/util";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  PermissionRequest,
  isPermissionType,
} from "@p0security/shared/types/permission";
import type { Policy } from "@p0security/shared/types/policy/types";
import {
  ActiveRequestStatuses,
  PendingRequestStatuses,
  RequestStatus,
  RequestStatuses,
  TerminalRequestStatuses,
  isFailedAccessRemoval,
} from "@p0security/shared/types/request-status";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Spin,
  Timeline,
  TimelineItemProps,
  Tooltip,
  Typography,
} from "@p0security/ui";
import { isKeyOf, isa } from "@p0security/util/types/is";
import { GraphTooltip } from "components/GraphTooltip";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import { DetailsDescriptions } from "components/Jit/DetailsDescriptions";
import { InstalledIntegrationsProvider } from "components/Jit/Policy/providers/InstalledIntegrationsProvider";
import SimpleTableView from "components/Jit/Policy/views/SimpleTableView";
import { FormattedDateTime } from "components/common/FormattedDateTime";
import { Identity } from "components/common/Identity";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { format, formatDistance } from "date-fns";
import { capitalize, startCase } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { colors } from "styles/variables";

import { AccessProperties } from "../AccessProperties.tsx";
import {
  extractApproverName,
  getNotificationApprovalLinks,
  getRequestEvents,
  handleRequestActionResponse,
  progressMessages,
  statusDescriptions,
} from "../requestUtils";
import { AgenticDetails } from "./AgenticDetails";
import { NotifierConversationLinks } from "./ConversationLink";
import { RequestNotFound } from "./RequestNotFound";
import { RequestStatusTag } from "./RequestStatusTag";
import { SessionHistoryButton } from "./SessionHistoryButton";

const statusToTimelineColor = (status: RequestStatus) => {
  switch (status) {
    case "CLEANUP_ERRORED":
    case "ERRORED":
    case "ERRORED_ERRORED":
      return colors.tagColors.red.text;
    case "APPROVED":
    case "NEW":
    case "DONE":
      return colors.tagColors.blue.text;
    case "APPROVED_NOTIFIED":
    case "CLEANED_UP":
    case "CLEANUP_SUBMITTED":
    case "DENIED":
    case "DENIED_NOTIFIED":
    case "DONE_NOTIFIED":
    case "DRAFT":
    case "ERRORED_NOTIFIED":
    case "EXPIRED":
    case "EXPIRED_NOTIFIED":
    case "EXPIRY_SUBMITTED":
    case "PENDING_APPROVAL":
    case "PENDING_APPROVAL_ESCALATED":
    case "REVOKE_SUBMITTED":
    case "REVOKED":
    case "REVOKED_NOTIFIED":
    case "STAGED":
    case "TRANSLATED":
    case "TIMED_OUT":
    case "TIMED_OUT_NOTIFIED":
      return colors.tagColors.grey.bg;
    default:
      return colors.tagColors.yellow.text;
  }
};

const RequestDescriptionDiv = (props: {
  request: PermissionRequest;
  status?: string; // optionally, render a description for a past status
}) => {
  const displayStatus = props.status ?? props.request.status;
  return (
    <div>
      {displayStatus === "PENDING_APPROVAL" ? (
        <NotifierApprovalLinks request={props.request} />
      ) : isa(RequestStatuses, displayStatus) ? (
        statusDescriptions[displayStatus](props.request)
      ) : (
        "Unknown"
      )}
    </div>
  );
};

const NotifierApprovalLinks: React.FC<{
  request: PermissionRequest;
}> = ({ request }) => {
  const links = getNotificationApprovalLinks(request.notifications);
  const approvalLinks = useMemo(() => {
    return (
      <>
        {" "}
        to:{" "}
        {links.map((link, index) => (
          <React.Fragment key={link.label}>
            {link.url ? <a href={link.url}>{link.label}</a> : link.label}
            {index < links.length - 1 && ", "}
          </React.Fragment>
        ))}
      </>
    );
  }, [links]);
  return <>Sent request for approval{links.length ? approvalLinks : ""}</>;
};

const principalTypeLabel = (request: PermissionRequest): string => {
  if (request.agentic?.headless) return "Agent";
  // gcloud alone declares a type, and `toMember` binds an unset one as `user:`.
  const declared = request.permission?.["principal-type"];
  return startCase(
    typeof declared === "string" && declared ? declared : "user"
  );
};

export const RequestLogs: React.FC<{ requestId: string; tenantId: string }> = ({
  tenantId,
  requestId,
}) => {
  const { orgSlug } = useParams();
  const { docs: partialDocs } = useFirestoreCollection<PermissionRequest>(
    `o/${tenantId}/permission-requests/${requestId}/logs`,
    { live: true }
  );
  const { doc, loading } = useFirestoreDoc<PermissionRequest>(
    `o/${tenantId}/permission-requests/${requestId}`,
    { live: true }
  );
  const request = useMemo(() => doc?.data, [doc]);

  const [retryingRevoke, setRetryingRevoke] = useState(false);
  const retryFetch = useAuthFetch(undefined, setRetryingRevoke);
  const onRetryRevoke = useCallback(async () => {
    handleRequestActionResponse(
      await retryFetch(`permission-requests/${requestId}/revoke`, {
        method: "POST",
      }),
      "Retrying revoke"
    );
  }, [requestId, retryFetch]);

  const reason = request?.reason?.trim();

  const principal = request ? toRequestAttribution(request).requestor : null;

  const policySnapshot: Policy[] =
    request?.policiesSnapshot ??
    // Backwards compatibility for requests created prior to routingRule -> policy rename on 2026/03/25
    (request as any)?.routingRulesSnapshot ??
    [];

  const { partialStatusDocs, completed } = useMemo(() => {
    if (!partialDocs) return { partialStatusDocs: [], isFinished: false };
    const { events, isComplete } = getRequestEvents(
      doc?.data,
      partialDocs ?? []
    );
    return { partialStatusDocs: events, completed: isComplete };
  }, [partialDocs, doc]);

  const timelineItems: TimelineItemProps[] = useMemo(() => {
    return (
      partialStatusDocs?.map((partialRequestDoc) => ({
        color: statusToTimelineColor(partialRequestDoc.initialStatus),
        key: partialRequestDoc.initialStatus,
        children: (
          <>
            <FormattedDateTime
              timestamp={partialRequestDoc.updates?.lastUpdatedTimestamp ?? 0}
            />
            <RequestDescriptionDiv
              request={request}
              status={partialRequestDoc.initialStatus}
            />
          </>
        ),
      })) ?? []
    );
  }, [partialStatusDocs, request]);

  return (
    <>
      {request && isPermissionType(request.type) ? (
        <Flex vertical gap={16}>
          <Card
            title={
              <Flex align="center" gap={8}>
                {AllIntegrationLogos[request.type]}
                Request Info
              </Flex>
            }
          >
            <DetailsDescriptions>
              <Descriptions.Item label="Request ID">
                {requestId}
              </Descriptions.Item>
              <Descriptions.Item label="Principal Type">
                {principalTypeLabel(request)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Tooltip title="The identity that receives access">
                    Principal
                  </Tooltip>
                }
              >
                {principal ? (
                  <Identity
                    value={{
                      type: principal.type,
                      sub: principal.sub,
                      role: "principal",
                    }}
                  />
                ) : (
                  "(unknown)"
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Tooltip title="The identity that made this access request">
                    Requestor
                  </Tooltip>
                }
              >
                {request.requestor === request.principal ? (
                  <Typography.Text type="secondary">
                    (requested by principal)
                  </Typography.Text>
                ) : (
                  request.requestor
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Approver">
                {extractApproverName(request, true)}
              </Descriptions.Item>
              <Descriptions.Item label="Reason">
                <Typography.Text
                  type={reason ? undefined : "secondary"}
                  italic={!reason}
                >
                  {reason ?? "No reason provided"}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Requested At">
                <FormattedDateTime timestamp={request.requestedTimestamp} />
              </Descriptions.Item>
              <Descriptions.Item label="Granted At">
                {request.grantTimestamp ? (
                  <FormattedDateTime timestamp={request.grantTimestamp} />
                ) : (
                  `Not Available`
                )}
              </Descriptions.Item>
              {/* A request whose expiry or revoke failed never actually
                  expired, and it flips between "active" and "terminal"
                  buckets as it moves ERRORED -> ERRORED_NOTIFIED, so
                  suppress the "Expired At" row to keep the layout stable.
                  Other errored terminal requests (e.g. cleanup failures)
                  still render normal expiry/revocation timestamps. */}
              {isa(TerminalRequestStatuses, request.status) &&
                !isFailedAccessRemoval(request) &&
                (request.revokedTimestamp || request.expiryTimestamp) && (
                  <Descriptions.Item label={"Expired At"}>
                    {request.revokedTimestamp ? (
                      <FormattedDateTime timestamp={request.revokedTimestamp} />
                    ) : request.expiryTimestamp ? (
                      <FormattedDateTime timestamp={request.expiryTimestamp} />
                    ) : (
                      `Not Available`
                    )}
                  </Descriptions.Item>
                )}
              {isa(PendingRequestStatuses, request.status) &&
              request.duration ? (
                request.originalRequestedDuration ? (
                  [
                    <Descriptions.Item
                      key="requested-duration"
                      label={"Requested Duration"}
                    >
                      {
                        convertToDurationOption(
                          request.originalRequestedDuration
                        ).value
                      }
                    </Descriptions.Item>,
                    <Descriptions.Item
                      key="modified-requested-duration"
                      label={
                        <Tooltip title="Reduced to the maximum allowable duration for this organization.">
                          Modified Requested Duration
                        </Tooltip>
                      }
                    >
                      {convertToDurationOption(request.duration).value}
                    </Descriptions.Item>,
                  ]
                ) : (
                  <Descriptions.Item label={"Requested Duration"}>
                    {convertToDurationOption(request.duration).value}
                  </Descriptions.Item>
                )
              ) : (isa(ActiveRequestStatuses, request.status) ||
                  isFailedAccessRemoval(request)) &&
                request.expiryTimestamp ? (
                <Descriptions.Item label={"Expires In"}>
                  <GraphTooltip
                    key="expiry"
                    title={format(request.expiryTimestamp, "PPpp")}
                  >
                    <Typography.Text>
                      {capitalize(
                        formatDistance(request.expiryTimestamp, Date.now())
                      )}
                    </Typography.Text>
                  </GraphTooltip>
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label={"Access Duration"}>
                  {request.grantTimestamp && request.revokedTimestamp
                    ? formatDistance(
                        request.revokedTimestamp,
                        request.grantTimestamp
                      )
                    : `Not Available`}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <div style={{ maxWidth: "15em" }}>
                  <RequestStatusTag request={request} />
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Notifications">
                <NotifierConversationLinks
                  notifications={request.notifications}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Most Recent Action">
                <RequestDescriptionDiv request={request} />
              </Descriptions.Item>
              {request.error && (
                <Descriptions.Item label="Error" span={2}>
                  <Flex vertical gap={8} align="flex-start">
                    <div className="pre">{request.error.message}</div>
                    {isFailedAccessRemoval(request) && (
                      <Button
                        type="primary"
                        danger
                        icon={<ReloadOutlined />}
                        loading={retryingRevoke}
                        onClick={onRetryRevoke}
                      >
                        Retry revoke
                      </Button>
                    )}
                  </Flex>
                </Descriptions.Item>
              )}
            </DetailsDescriptions>
          </Card>
          {request.agentic && (
            <AgenticDetails
              agentic={request.agentic}
              subject={request.principal}
            />
          )}
          <SessionHistoryButton
            requestId={requestId}
            requestType={request.type}
          />
          {isPermissionType(request.type) && (
            <Card title="Access Properties">
              <AccessProperties request={request} />
            </Card>
          )}
          <Card title="History">
            <Timeline
              pending={
                !completed && isKeyOf(progressMessages, request.status)
                  ? progressMessages[request.status]
                  : null
              }
              reverse={true}
              items={timelineItems}
            />
          </Card>
          <Card title="Policies snapshot">
            <Typography.Paragraph>
              This is a snapshot of the policies that existed at the time the
              request was created <em>and</em> which applied to the requestor or
              the resource. The policies configuration may have changed since
              this request was created; the current configuration can be found
              in the{" "}
              <TenantAwareLink to={AppPaths.Policies}>
                Policy Studio
              </TenantAwareLink>
              .
            </Typography.Paragraph>
            {policySnapshot.length === 0 ? (
              <Alert
                message="Policies snapshot not available"
                description="This request is old and did not capture a policies snapshot."
                type="warning"
                showIcon
              />
            ) : (
              <InstalledIntegrationsProvider>
                <SimpleTableView jsonRules={policySnapshot} />
              </InstalledIntegrationsProvider>
            )}
          </Card>
        </Flex>
      ) : loading ? (
        <Spin />
      ) : (
        <RequestNotFound orgSlug={orgSlug} />
      )}
    </>
  );
};
